// src/contexts/TransactionsContext.js
import React, { createContext, useContext, useEffect, useState } from 'react'
import { fetchTransactions } from '../services/transactionsService'

const TransactionsContext = createContext(null)
const BASE_STORAGE_KEY = 'mojo_transactions_v1'

const getStorageKey = () => {
    try {
        const rawUser = localStorage.getItem('session_user')
        const parsedUser = rawUser ? JSON.parse(rawUser) : null
        const userKey =
            parsedUser?.id ||
            parsedUser?.email ||
            parsedUser?.phone ||
            localStorage.getItem('token') ||
            'guest'

        return `${BASE_STORAGE_KEY}:${String(userKey)}`
    } catch (error) {
        return `${BASE_STORAGE_KEY}:guest`
    }
}

const makeId = () => {
    try {
        return crypto.randomUUID()
    } catch (e) {
        return `tx_${Date.now()}_${Math.random().toString(16).slice(2)}`
    }
}

export const TransactionsProvider = ({ children }) => {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const storageKey = getStorageKey()

    // load from localStorage first (fast UI)
    useEffect(() => {
        try {
            const raw = localStorage.getItem(storageKey)
            if (!raw) return
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed)) setItems(parsed)
        } catch (e) {
            setItems([])
        }
    }, [storageKey])

    // persist
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(items))
        } catch (e) {
            // ignore
        }
    }, [items, storageKey])

    const setTransactions = (list) => {
        const safe = Array.isArray(list) ? list : []
        setItems(safe)
        try {
            localStorage.setItem(storageKey, JSON.stringify(safe))
        } catch (e) {
            // ignore
        }
    }

    const addTransaction = (tx) => {
        const safeTx = {
            id: tx?.id || makeId(),
            createdAt: tx?.createdAt || new Date().toISOString(),
            type: tx?.type || 'payment',
            status: tx?.status || 'completed',
            ...tx,
        }

        setItems((prev) => [safeTx, ...prev])
        return safeTx
    }

    const clearTransactions = () => {
        setItems([])
        try {
            localStorage.removeItem(storageKey)
        } catch (e) {
            // ignore
        }
    }

    const getById = (id) => items.find((t) => t.id === id)

    const loadFromApi = async () => {
        setLoading(true)
        setError(null)
        try {
            const list = await fetchTransactions()
            setTransactions(list)
        } catch (e) {
            setError(e?.message || 'Failed to load transactions')
        } finally {
            setLoading(false)
        }
    }

    const value = {
        transactions: items,
        setTransactions,
        addTransaction,
        clearTransactions,
        getById,
        loadFromApi,
        loading,
        error,
    }

    return <TransactionsContext.Provider value={value}>{children}</TransactionsContext.Provider>
}

export const useTransactions = () => {
    const ctx = useContext(TransactionsContext)
    if (!ctx) throw new Error('useTransactions must be used inside TransactionsProvider')
    return ctx
}

export const useTransactionsSafe = () => useContext(TransactionsContext)

export default TransactionsContext
